import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { User } from '@/data/models/user.interface';
import authService from '@/data/services/auth.service';
import useUserStore from '@/data/stores/user.store';
import { createScudRedirectUrl } from '@/utils/auth/url.utils';

export const AUTH_QUERY_KEY = ['auth', 'user'];

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, clearUser, hasPermission, isAuthenticated } = useUserStore();
  const skipAuth = process.env.VITE_APP_SKIP_AUTH === 'true';

  // State for reactive auth error
  const [authError, setAuthError] = useState(() => authService.getAuthError());

  // Check if we should enable auth query on mount
  const [shouldEnableQuery, setShouldEnableQuery] = useState(() => {
    const hasToken = !!authService.getToken();
    const hasBearerToken = !!process.env.VITE_APP_BEARER_TOKEN;
    const cachedUser = useUserStore.getState().user;

    // If we have a cached authenticated user with a valid token, skip the query
    if (hasToken && cachedUser?.isAuthenticated) {
      console.log(
        '✅ Using cached authenticated user, skipping initial API call',
      );
      return false;
    }

    return skipAuth || hasBearerToken || hasToken;
  });

  // Subscribe to auth error changes
  useEffect(() => {
    const updateAuthError = () => {
      const currentError = authService.getAuthError();
      setAuthError(currentError);
    };

    // Subscribe to auth error changes
    const unsubscribe = authService.addAuthErrorListener(updateAuthError);

    // Cleanup subscription
    return unsubscribe;
  }, []);

  // Monitor for new tokens (e.g., after login redirect) and enable query if needed
  useEffect(() => {
    const hasToken = !!authService.getToken();
    const cachedUser = useUserStore.getState().user;

    // If we have a token but no authenticated user, we need to fetch user data
    if (hasToken && !cachedUser?.isAuthenticated && !shouldEnableQuery) {
      console.log('🔄 Token found without authenticated user, enabling query');
      setShouldEnableQuery(true);
    }
  }, [shouldEnableQuery]);

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      const userData = await authService.getMe();
      setUser(userData);
      // Clear auth error if successful
      if (userData.isAuthenticated) {
        setAuthError(null);
      } else {
        // If auth failed, disable future queries until re-login
        setShouldEnableQuery(false);
      }
      return userData;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: shouldEnableQuery,
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      authService.logout();
      clearUser();
    },
    onSuccess: () => {
      queryClient.clear();
      navigate({ to: '/' });
    },
  });

  const initializeAuth = () => {
    authService.initializeToken();
  };

  const checkPermission = (permission: string): boolean => {
    return hasPermission(permission);
  };

  const getToken = (): string | null => {
    return authService.getToken();
  };

  const login = () => {
    // Clear any existing auth error before attempting login
    authService.clearAuthError();
    setAuthError(null);

    // Re-enable query for new login attempt
    setShouldEnableQuery(true);

    // Manage VITE_APP_BEARER_TOKEN authentication to avoid scud redirection to Trustelem
    if (process.env.VITE_APP_BEARER_TOKEN) {
      window.location.reload();
      return;
    }

    const loginUri = createScudRedirectUrl(
      window.location.href,
      String(process.env.VITE_APP_LOGIN_URL),
    );
    window.location.href = loginUri;
  };

  return {
    user: user || useUserStore.getState().user,
    isLoading,
    error,
    authError,
    isAuthenticated: isAuthenticated(),
    refetch,
    logout: logoutMutation.mutate,
    login,
    initializeAuth,
    checkPermission,
    getToken,
  };
};

export const useCurrentUser = (): User => {
  const { user } = useAuth();
  return user;
};

export const usePermission = (permission: string): boolean => {
  const { checkPermission } = useAuth();
  return checkPermission(permission);
};

export const useAuthCheck = () => {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
};
